/**
 * Episode Chat Module
 * Handles AI chat functionality for episode view
 */

import { API } from '../core/api.js';
import { showNotification } from '../components/notifications.js';
import { showConfirmModal, showPromptModal } from '../components/modal.js';
import { escapeHtml } from '../core/utils.js';
import { createChatThinkingElement } from '../components/loading.js';

// Chat state
let chatHistory = [];
let activeChatIdx = null; // null = current/new chat, 0+ = saved chat index
let selectedModel = null; // Currently selected LLM model
let availableModels = []; // Cached list of available models

/**
 * Get current chat history
 */
export function getChatHistory() {
    return chatHistory;
}

/**
 * Fetch available LLM models
 */
export async function fetchAvailableModels() {
    if (availableModels.length > 0) {
        return availableModels;
    }

    try {
        const response = await API.get('/api/llm/models');
        if (response.models && response.models.length > 0) {
            availableModels = response.models;
            // Set default model to first one if not already set
            if (!selectedModel && availableModels.length > 0) {
                selectedModel = availableModels[0].id;
            }
        }
        return availableModels;
    } catch (error) {
        console.error('Error fetching models:', error);
        return [];
    }
}

/**
 * Get selected model
 */
export function getSelectedModel() {
    return selectedModel;
}

/**
 * Set selected model
 */
export function setSelectedModel(modelId) {
    selectedModel = modelId;
    // Update dropdown display
    const btn = document.getElementById('model-selector-btn');
    if (btn && modelId) {
        const model = availableModels.find(m => m.id === modelId);
        btn.textContent = model ? model.name : modelId.split('/').pop();
    }
}

/**
 * Set chat history (called when loading episode)
 */
export function setChatHistory(history) {
    chatHistory = history || [];
}

/**
 * Render chat interface with saved conversations support
 */
export function renderChatInterface(episode) {
    const hasTranscription = episode.has_transcription ?? (episode.transcription && episode.transcription.length > 0);
    const llmStatus = episode.llm_status || 'pending';

    if (!hasTranscription) {
        return `
            <div class="chat-disabled">
                <p>Chat is available after transcription is complete.</p>
            </div>
        `;
    }

    const chatData = episode.chat_history || {};
    const savedChats = chatData.saved || [];
    const chatCount = savedChats.length + (chatHistory.length > 0 ? 1 : 0);

    return `
        <div class="chat-container" id="chat-container">
            <div class="chat-header">
                <div class="chat-header-left">
                    <button class="btn btn-small btn-secondary" onclick="toggleChatMaximize()" id="chat-maximize-btn" title="Expand chat">
                        +
                    </button>
                    <div class="chat-dropdown">
                        <button class="btn btn-small" onclick="toggleChatDropdown(event)" id="chat-dropdown-btn">
                            ▼ Chats${chatCount > 0 ? ` (${chatCount})` : ''}
                        </button>
                        <div class="chat-dropdown-menu" id="chat-dropdown-menu">
                            <div class="chat-dropdown-item new-chat" onclick="startNewChat(${episode.id})">
                                + New Chat
                            </div>
                            ${chatHistory.length > 0 || savedChats.length > 0 ? '<div class="chat-dropdown-divider"></div>' : ''}
                            ${chatHistory.length > 0 && activeChatIdx === null ? `
                                <div class="chat-dropdown-item active" onclick="switchToCurrentChat()">
                                    <span class="chat-item-title">● Current Chat</span>
                                </div>
                            ` : ''}
                            ${savedChats.map((chat, idx) => `
                                <div class="chat-dropdown-item${activeChatIdx === idx ? ' active' : ''}" onclick="switchToSavedChat(${episode.id}, ${idx})">
                                    <span class="chat-item-title">${escapeHtml(chat.title || 'Chat ' + (idx + 1))}</span>
                                    <button class="chat-item-delete" onclick="event.stopPropagation(); deleteSavedChat(${episode.id}, ${idx})" title="Delete">×</button>
                                </div>
                            `).join('')}
                        </div>
                    </div>
                </div>
                <div class="chat-header-buttons">
                    <button class="btn btn-small" onclick="clearChat()" title="Clear current chat">
                        Clear
                    </button>
                    <button class="btn btn-small" onclick="saveChat(${episode.id})" title="Save current chat">
                        Save
                    </button>
                    ${llmStatus !== 'completed' ? `
                        <button class="btn btn-small" onclick="analyzeEpisode(${episode.id})" title="Run LLM analysis">
                            Analyze
                        </button>
                    ` : ''}
                </div>
            </div>
            <div class="chat-messages" id="chat-messages">
                <div class="chat-welcome">
                    <p>Ask questions about the episode content. The AI has access to the full transcript.</p>
                </div>
            </div>
            <div class="chat-input-area">
                <div class="chat-input-row">
                    <textarea id="chat-input"
                              placeholder="Ask a question..."
                              onkeydown="handleChatKeydown(event)"></textarea>
                    <button class="btn btn-primary chat-send-btn" onclick="sendChatMessage()">
                        Send
                    </button>
                </div>
                <div class="chat-model-selector">
                    <span class="model-label">Model:</span>
                    <div class="model-dropdown">
                        <button class="model-dropdown-btn" id="model-selector-btn" onclick="toggleModelDropdown(event)">
                            Loading...
                        </button>
                        <div class="model-dropdown-menu" id="model-dropdown-menu"></div>
                    </div>
                </div>
            </div>
        </div>
    `;
}

/**
 * Setup chat event handlers
 */
export async function setupChatHandlers(episode) {
    const chatInput = document.getElementById('chat-input');
    if (chatInput) {
        chatInput.addEventListener('input', function() {
            this.style.height = 'auto';
            this.style.height = Math.min(this.scrollHeight, 150) + 'px';
        });
    }

    // Fetch and populate model selector
    await populateModelSelector();
}

/**
 * Populate model selector dropdown
 */
async function populateModelSelector() {
    const models = await fetchAvailableModels();
    const btn = document.getElementById('model-selector-btn');
    const menu = document.getElementById('model-dropdown-menu');

    if (!btn || !menu) return;

    if (models.length === 0) {
        btn.textContent = 'No models';
        btn.disabled = true;
        return;
    }

    // Set button text to current model
    const currentModel = models.find(m => m.id === selectedModel) || models[0];
    if (!selectedModel) selectedModel = currentModel.id;
    btn.textContent = currentModel.name;

    // Build menu
    menu.innerHTML = models.map(model => `
        <div class="model-dropdown-item${model.id === selectedModel ? ' active' : ''}"
             onclick="selectModel('${escapeHtml(model.id)}')">
            ${escapeHtml(model.name)}
        </div>
    `).join('');
}

/**
 * Toggle model dropdown menu
 */
export function toggleModelDropdown(event) {
    if (event) event.stopPropagation();
    const menu = document.getElementById('model-dropdown-menu');
    if (!menu) return;

    const isOpen = menu.classList.toggle('active');

    if (isOpen) {
        const closeHandler = (e) => {
            if (!e.target.closest('.model-dropdown')) {
                menu.classList.remove('active');
                document.removeEventListener('click', closeHandler);
            }
        };
        setTimeout(() => document.addEventListener('click', closeHandler), 0);
    }
}

/**
 * Select a model from dropdown
 */
export function selectModel(modelId) {
    setSelectedModel(modelId);

    // Update active state in menu
    const menu = document.getElementById('model-dropdown-menu');
    if (menu) {
        menu.querySelectorAll('.model-dropdown-item').forEach(item => {
            item.classList.toggle('active', item.textContent.trim() === (availableModels.find(m => m.id === modelId)?.name || modelId));
        });
        menu.classList.remove('active');
    }

    showNotification(`Model: ${modelId.split('/').pop()}`, 'info');
}

/**
 * Load persisted chat history
 */
export function loadPersistedChat(episode) {
    if (episode.chat_history) {
        const chatData = episode.chat_history;
        if (Array.isArray(chatData)) {
            chatHistory = chatData;
        } else {
            chatHistory = chatData.current || [];
        }
        if (chatHistory.length > 0) {
            renderChatMessages();
        }
    } else {
        chatHistory = [];
    }
}

/**
 * Handle keyboard input in chat
 */
export function handleChatKeydown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        sendChatMessage();
    }
}

/**
 * Send chat message
 */
export async function sendChatMessage(currentEpisodeId) {
    const input = document.getElementById('chat-input');
    const messagesContainer = document.getElementById('chat-messages');

    if (!input || !messagesContainer) return;

    const message = input.value.trim();
    if (!message) return;

    const episodeId = currentEpisodeId || window.currentEpisodeId;
    if (!episodeId) return;

    input.value = '';
    input.style.height = 'auto';

    const welcome = messagesContainer.querySelector('.chat-welcome');
    if (welcome) welcome.remove();

    appendChatMessage('user', message);

    // Create animated thinking element with rotating phrases
    const { element: thinkingElement, intervalId: thinkingInterval } = createChatThinkingElement();
    const loadingId = 'chat-loading-' + Date.now();
    thinkingElement.id = loadingId;
    messagesContainer.appendChild(thinkingElement);
    messagesContainer.scrollTop = messagesContainer.scrollHeight;

    try {
        const response = await API.post(`/api/episodes/${episodeId}/chat`, {
            message: message,
            history: chatHistory,
            persist: true,
            model: selectedModel  // Pass selected model
        });

        clearInterval(thinkingInterval);
        const loadingEl = document.getElementById(loadingId);
        if (loadingEl) loadingEl.remove();

        chatHistory = response.history;
        appendChatMessage('assistant', response.response, {
            model: response.model,
            timestamp: response.timestamp
        });

    } catch (error) {
        console.error('Chat error:', error);
        clearInterval(thinkingInterval);
        const loadingEl = document.getElementById(loadingId);
        if (loadingEl) loadingEl.remove();
        appendChatMessage('error', 'Failed to get response. Please try again.');
    }
}

/**
 * Append a message to the chat UI
 */
export function appendChatMessage(role, content, metadata = null) {
    const messagesContainer = document.getElementById('chat-messages');
    if (!messagesContainer) return;

    const msgDiv = document.createElement('div');
    msgDiv.className = `chat-message ${role}`;

    // Message content
    const contentDiv = document.createElement('div');
    contentDiv.className = 'chat-message-content';
    contentDiv.textContent = content;
    msgDiv.appendChild(contentDiv);

    // Add metadata for assistant messages (model + time)
    if (role === 'assistant' && metadata) {
        const metaDiv = document.createElement('div');
        metaDiv.className = 'chat-message-meta';

        const parts = [];
        if (metadata.model) {
            parts.push(metadata.model);
        }
        if (metadata.timestamp) {
            const date = new Date(metadata.timestamp);
            parts.push(date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
        }

        if (parts.length > 0) {
            metaDiv.textContent = parts.join(' · ');
            msgDiv.appendChild(metaDiv);
        }
    }

    messagesContainer.appendChild(msgDiv);
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
}

/**
 * Render persisted chat messages
 */
export function renderChatMessages() {
    const messagesContainer = document.getElementById('chat-messages');
    if (!messagesContainer || chatHistory.length === 0) return;

    const welcome = messagesContainer.querySelector('.chat-welcome');
    if (welcome) welcome.remove();

    chatHistory.forEach(msg => {
        const metadata = msg.role === 'assistant' ? {
            model: msg.model,
            timestamp: msg.timestamp
        } : null;
        appendChatMessage(msg.role, msg.content, metadata);
    });
}

/**
 * Clear current chat (discard without saving)
 */
export async function clearChat() {
    const episodeId = window.currentEpisodeId;

    if (chatHistory.length === 0) {
        showNotification('Chat is already empty', 'info');
        return;
    }

    const confirmed = await showConfirmModal({
        title: 'Clear Chat',
        message: 'Discard current conversation without saving?',
        confirmText: 'Clear',
        cancelText: 'Cancel',
        confirmClass: 'btn-danger',
        icon: '🗑️'
    });

    if (!confirmed) return;

    // Clear local state
    chatHistory = [];
    activeChatIdx = null;

    // Clear persisted chat in database
    if (episodeId) {
        try {
            await API.post(`/api/episodes/${episodeId}/chat`, { clear: true });
        } catch (error) {
            console.error('Error clearing persisted chat:', error);
        }
    }

    const messagesContainer = document.getElementById('chat-messages');
    if (messagesContainer) {
        messagesContainer.textContent = '';
        const welcomeDiv = document.createElement('div');
        welcomeDiv.className = 'chat-welcome';
        const p = document.createElement('p');
        p.textContent = 'Ask questions about the episode content. The AI has access to the full transcript.';
        welcomeDiv.appendChild(p);
        messagesContainer.appendChild(welcomeDiv);
    }

    showNotification('Chat cleared', 'success');
}

/**
 * Start a new chat (archives current to saved list)
 */
export async function newChat(episodeId, openEpisodeViewFn) {
    if (chatHistory.length > 0) {
        const save = await showConfirmModal({
            title: 'New Chat',
            message: 'Save current conversation before starting new?',
            confirmText: 'Save',
            cancelText: 'Discard',
            icon: '💬'
        });
        if (save) {
            await saveChat(episodeId, openEpisodeViewFn);
        }
    }

    chatHistory = [];
    await API.post(`/api/episodes/${episodeId}/chat/new`, {});

    const messagesContainer = document.getElementById('chat-messages');
    if (messagesContainer) {
        messagesContainer.textContent = '';
        const welcomeDiv = document.createElement('div');
        welcomeDiv.className = 'chat-welcome';
        const p = document.createElement('p');
        p.textContent = 'Ask questions about the episode content. The AI has access to the full transcript.';
        welcomeDiv.appendChild(p);
        messagesContainer.appendChild(welcomeDiv);
    }

    showNotification('New chat started', 'success');
}

/**
 * Save current chat
 */
export async function saveChat(episodeId, openEpisodeViewFn) {
    if (chatHistory.length === 0) {
        showNotification('No messages to save', 'info');
        return;
    }

    const defaultName = chatHistory[0]?.content?.substring(0, 30) + '...' || 'Saved chat';
    const title = await showPromptModal({
        title: 'Save Chat',
        message: 'Name this conversation:',
        placeholder: 'Enter chat name...',
        defaultValue: defaultName,
        confirmText: 'Save',
        cancelText: 'Cancel',
        icon: '💾'
    });
    if (!title) return;

    try {
        await API.post(`/api/episodes/${episodeId}/chat/save`, {
            title: title,
            messages: chatHistory
        });
        showNotification('Chat saved!', 'success');
        if (openEpisodeViewFn) openEpisodeViewFn(episodeId);
    } catch (error) {
        console.error('Error saving chat:', error);
        showNotification('Failed to save chat', 'error');
    }
}

/**
 * Load a saved chat
 */
export async function loadSavedChat(episodeId, chatIdx) {
    try {
        const response = await API.get(`/api/episodes/${episodeId}/chat/saved/${chatIdx}`);
        chatHistory = response.messages || [];
        renderChatMessages();
        showNotification('Chat loaded', 'success');
    } catch (error) {
        console.error('Error loading chat:', error);
        showNotification('Failed to load chat', 'error');
    }
}

/**
 * Delete a saved chat
 */
export async function deleteSavedChat(episodeId, chatIdx, openEpisodeViewFn) {
    const confirmed = await showConfirmModal({
        title: 'Delete Chat',
        message: 'Are you sure you want to delete this saved conversation?',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        confirmClass: 'btn-danger',
        icon: '🗑️'
    });
    if (!confirmed) return;

    try {
        await API.delete(`/api/episodes/${episodeId}/chat/saved/${chatIdx}`);
        showNotification('Chat deleted', 'success');
        if (openEpisodeViewFn) openEpisodeViewFn(episodeId);
    } catch (error) {
        console.error('Error deleting chat:', error);
        showNotification('Failed to delete chat', 'error');
    }
}

/**
 * Trigger LLM analysis
 */
export async function analyzeEpisode(episodeId, currentEpisodeIdFn, openEpisodeViewFn) {
    try {
        showNotification('Starting LLM analysis...', 'info');
        await API.post(`/api/episodes/${episodeId}/analyze`, {});
        showNotification('Analysis complete!', 'success');

        const currentId = currentEpisodeIdFn ? currentEpisodeIdFn() : window.currentEpisodeId;
        if (currentId === episodeId && openEpisodeViewFn) {
            openEpisodeViewFn(episodeId);
        }
    } catch (error) {
        console.error('Analysis error:', error);
        showNotification('Analysis failed: ' + error.message, 'error');
    }
}

// Track maximized state
let isChatMaximized = false;

/**
 * Toggle chat between normal sidebar view and maximized full-screen modal
 */
export function toggleChatMaximize() {
    const chatContainer = document.getElementById('chat-container');
    const maximizeBtn = document.getElementById('chat-maximize-btn');

    if (!chatContainer) return;

    isChatMaximized = !isChatMaximized;

    if (isChatMaximized) {
        // Create overlay backdrop
        const overlay = document.createElement('div');
        overlay.id = 'chat-maximize-overlay';
        overlay.className = 'chat-maximize-overlay';
        overlay.addEventListener('click', toggleChatMaximize);
        document.body.appendChild(overlay);

        // Maximize chat
        chatContainer.classList.add('chat-maximized');
        document.body.style.overflow = 'hidden';

        // Update button text
        if (maximizeBtn) {
            maximizeBtn.textContent = '−';
            maximizeBtn.title = 'Minimize chat';
        }
    } else {
        // Remove overlay
        const overlay = document.getElementById('chat-maximize-overlay');
        if (overlay) overlay.remove();

        // Minimize chat
        chatContainer.classList.remove('chat-maximized');
        document.body.style.overflow = '';

        // Update button text
        if (maximizeBtn) {
            maximizeBtn.textContent = '+';
            maximizeBtn.title = 'Expand chat';
        }
    }
}

/**
 * Toggle chat dropdown menu
 */
export function toggleChatDropdown(event) {
    if (event) event.stopPropagation();
    const menu = document.getElementById('chat-dropdown-menu');
    if (!menu) return;

    const isOpen = menu.classList.toggle('active');

    // Close dropdown when clicking outside
    if (isOpen) {
        const closeHandler = (e) => {
            if (!e.target.closest('.chat-dropdown')) {
                menu.classList.remove('active');
                document.removeEventListener('click', closeHandler);
            }
        };
        setTimeout(() => document.addEventListener('click', closeHandler), 0);
    }
}

/**
 * Start a new empty chat
 */
export function startNewChat(episodeId) {
    // Close dropdown
    const menu = document.getElementById('chat-dropdown-menu');
    if (menu) menu.classList.remove('active');

    // Clear current chat
    chatHistory = [];
    activeChatIdx = null;

    // Clear messages UI
    const messagesContainer = document.getElementById('chat-messages');
    if (messagesContainer) {
        messagesContainer.textContent = '';
        const welcomeDiv = document.createElement('div');
        welcomeDiv.className = 'chat-welcome';
        const p = document.createElement('p');
        p.textContent = 'Ask questions about the episode content. The AI has access to the full transcript.';
        welcomeDiv.appendChild(p);
        messagesContainer.appendChild(welcomeDiv);
    }

    showNotification('New chat started', 'success');
}

/**
 * Switch to current (unsaved) chat
 */
export function switchToCurrentChat() {
    // Close dropdown
    const menu = document.getElementById('chat-dropdown-menu');
    if (menu) menu.classList.remove('active');

    activeChatIdx = null;
    renderChatMessages();
}

/**
 * Switch to a saved chat
 */
export async function switchToSavedChat(episodeId, chatIdx) {
    // Close dropdown
    const menu = document.getElementById('chat-dropdown-menu');
    if (menu) menu.classList.remove('active');

    try {
        const response = await API.get(`/api/episodes/${episodeId}/chat/saved/${chatIdx}`);
        chatHistory = response.messages || [];
        activeChatIdx = chatIdx;

        // Clear and re-render messages
        const messagesContainer = document.getElementById('chat-messages');
        if (messagesContainer) {
            messagesContainer.textContent = '';
        }
        renderChatMessages();

        showNotification(`Loaded: ${response.title || 'Chat ' + (chatIdx + 1)}`, 'success');
    } catch (error) {
        console.error('Error loading chat:', error);
        showNotification('Failed to load chat', 'error');
    }
}
